// ===================================================================
// HMGP — State Priority Scoring
// Review, score, rank, and forward subapplications to FEMA
// Configurable scoring criteria stored in KV config
// ===================================================================

import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Title, Text, Paper, Group, Stack, Badge, Button, TextInput, NumberInput,
  Table, SimpleGrid, Collapse, ActionIcon, Tooltip, Loader, Center,
  Modal, Slider, Progress,
} from '../../../components/gab/GabComponents';
import {
  IconDownload, IconRefresh,
  IconDeviceFloppy, IconRocket, IconSend, IconSettings,
  IconArrowUp, IconArrowDown, IconMinus, IconPencil,
} from '@tabler/icons-react';
import { notifications } from '../../../lib/notifications';
import { useHMGPStore } from '../../../store/hmgpStore';
import { usePAGrantsStore } from '../../../store/paGrantsStore';
import { useActivePADisaster as useActiveDisaster } from '../../../hooks/useActiveDisaster';
import {
  HMGP_PROJECT_TYPE_LABELS, HMGP_PROJECT_TYPE_COLORS,
  HMGP_SUBAPP_STATUS_LABELS,
  type HMGPProjectType, type HMGPSubappStatus, type HMGPSubapplication,
} from '../../../types/pagrants';
import { exportToCSV } from '../../../lib/exportUtils';
import { WORKER_URL } from '../../../lib/api-switch';

// ── Constants ────────────────────────────────────────────────

const API = `${WORKER_URL}/api/config`;
const H = { 'Content-Type': 'application/json', Origin: 'https://apps.opengov.com' };
const HMGP_GREEN = '#059669';

const SUBAPP_STATUS_COLORS: Record<string, string> = {
  draft: 'gray', submitted: 'blue', under_review: 'yellow',
  prioritized: 'teal', forwarded_to_fema: 'green', returned: 'orange', withdrawn: 'red',
};

// ── Scoring criteria types ───────────────────────────────────

interface ScoringCriteria {
  bcaWeight: number;        // max 25
  repLossWeight: number;    // max 20
  critFacilWeight: number;  // max 15
  costEffWeight: number;    // max 15
  communityWeight: number;  // max 10
  sviWeight: number;        // max 15
}

const DEFAULT_CRITERIA: ScoringCriteria = {
  bcaWeight: 25,
  repLossWeight: 20,
  critFacilWeight: 15,
  costEffWeight: 15,
  communityWeight: 10,
  sviWeight: 15,
};

const CRITERIA_LABELS: Record<keyof ScoringCriteria, string> = {
  bcaWeight: 'BCA Ratio Score',
  repLossWeight: 'Repetitive Loss',
  critFacilWeight: 'Critical Facility',
  costEffWeight: 'Cost Effectiveness',
  communityWeight: 'Community Participation',
  sviWeight: 'Social Vulnerability',
};

const CRITERIA_DESCRIPTIONS: Record<keyof ScoringCriteria, string> = {
  bcaWeight: 'Based on Benefit-Cost Ratio. BCR >= 2.0 = full, >= 1.5 = 80%, >= 1.0 = 60%',
  repLossWeight: 'Severe Repetitive Loss (SRL) = full, Repetitive Loss = 75%',
  critFacilWeight: 'Is the project protecting a critical facility or infrastructure?',
  costEffWeight: 'Lower total cost per unit of mitigation = higher score',
  communityWeight: 'CRS participation, current Natural Hazard Mitigation Plan',
  sviWeight: 'CDC Social Vulnerability Index — High/Medium/Low',
};


// ── Auto-scoring logic ───────────────────────────────────────

function computeBCAScore(sub: HMGPSubapplication, maxPts: number): number {
  const bcr = parseFloat(sub.bcaRatio);
  if (isNaN(bcr) || bcr <= 0) return 0;
  if (bcr >= 2.0) return maxPts;
  if (bcr >= 1.5) return Math.round(maxPts * 0.8);
  if (bcr >= 1.0) return Math.round(maxPts * 0.6);
  return 0;
}

function computeRepLossScore(sub: HMGPSubapplication, maxPts: number): number {
  const notes = (sub.notes || '').toLowerCase();
  if (notes.includes('severe repetitive loss') || notes.includes('srl')) return maxPts;
  if (notes.includes('repetitive loss') || notes.includes('rl property')) return Math.round(maxPts * 0.75);
  return 0;
}

function computeCritFacilScore(sub: HMGPSubapplication, maxPts: number): number {
  const notes = (sub.notes || '').toLowerCase();
  if (notes.includes('critical facility') || notes.includes('critical infrastructure')) return maxPts;
  return 0;
}

function computeCostEffScore(sub: HMGPSubapplication, maxPts: number): number {
  // Lower cost per structure is better. Without numberOfStructures, use BCA as proxy.
  const cost = parseFloat(sub.estimatedCost);
  if (isNaN(cost) || cost <= 0) return 0;
  // Best-effort: if cost < 100k full, < 500k half, else quarter
  if (cost < 100_000) return maxPts;
  if (cost < 500_000) return Math.round(maxPts * 0.5);
  return Math.round(maxPts * 0.25);
}

function computeCommunityScore(sub: HMGPSubapplication, maxPts: number): number {
  const notes = (sub.notes || '').toLowerCase();
  let score = 0;
  if (notes.includes('crs') || notes.includes('community rating system')) score += maxPts * 0.5;
  if (notes.includes('nhmp') || notes.includes('hazard mitigation plan')) score += maxPts * 0.5;
  return Math.min(Math.round(score), maxPts);
}

function computeSVIScore(sub: HMGPSubapplication, maxPts: number): number {
  const notes = (sub.notes || '').toLowerCase();
  if (notes.includes('high svi') || notes.includes('svi: high')) return maxPts;
  if (notes.includes('medium svi') || notes.includes('svi: medium') || notes.includes('moderate svi')) return Math.round(maxPts * 0.67);
  if (notes.includes('low svi') || notes.includes('svi: low')) return Math.round(maxPts * 0.33);
  return 0;
}

function computeAutoScore(sub: HMGPSubapplication, criteria: ScoringCriteria): number {
  return (
    computeBCAScore(sub, criteria.bcaWeight) +
    computeRepLossScore(sub, criteria.repLossWeight) +
    computeCritFacilScore(sub, criteria.critFacilWeight) +
    computeCostEffScore(sub, criteria.costEffWeight) +
    computeCommunityScore(sub, criteria.communityWeight) +
    computeSVIScore(sub, criteria.sviWeight)
  );
}

function getFinalScore(sub: HMGPSubapplication, criteria: ScoringCriteria): number {
  const manual = parseFloat(sub.statePriorityScore);
  if (!isNaN(manual) && manual > 0) return manual;
  return computeAutoScore(sub, criteria);
}

// ── Criteria registry (references compute functions above) ───

const CRITERIA_REGISTRY: { key: string; field: keyof ScoringCriteria; compute: (sub: HMGPSubapplication, max: number) => number }[] = [
  { key: 'bca', field: 'bcaWeight', compute: computeBCAScore },
  { key: 'repLoss', field: 'repLossWeight', compute: computeRepLossScore },
  { key: 'critFacil', field: 'critFacilWeight', compute: computeCritFacilScore },
  { key: 'costEff', field: 'costEffWeight', compute: computeCostEffScore },
  { key: 'community', field: 'communityWeight', compute: computeCommunityScore },
  { key: 'svi', field: 'sviWeight', compute: computeSVIScore },
];

// ── Component ────────────────────────────────────────────────

export default function HMGPPriorityScoring() {
  const activeDisaster = useActiveDisaster();
  const disasterId = activeDisaster ? String(activeDisaster.Rid) : '';

  const organizations = usePAGrantsStore(s => s.organizations);
  const fetchOrgs = usePAGrantsStore(s => s.fetchOrganizations);

  const {
    subapplications, loadingSubs, fetchSubapplications, updateSubapplication,
  } = useHMGPStore();

  // ── Criteria config ──
  const [criteria, setCriteria] = useState<ScoringCriteria>(DEFAULT_CRITERIA);
  const [criteriaOpen, setCriteriaOpen] = useState(false);
  const [criteriaLoading, setCriteriaLoading] = useState(false);
  const [criteriaDraft, setCriteriaDraft] = useState<ScoringCriteria>(DEFAULT_CRITERIA);

  // ── Manual score overrides (local edits before save) ──
  const [manualScores, setManualScores] = useState<Record<number, string>>({});
  const [bulkN, setBulkN] = useState<number | ''>(10);
  const [saving, setSaving] = useState<Record<number, boolean>>({});

  // ── Score Detail modal ──
  const [scoringSubapp, setScoringSubapp] = useState<HMGPSubapplication | null>(null);
  const [criteriaScores, setCriteriaScores] = useState<Record<string, number>>({});
  const [scoringSaving, setScoringSaving] = useState(false);

  const openScoring = useCallback((sub: HMGPSubapplication) => {
    const scores: Record<string, number> = {};
    for (const c of CRITERIA_REGISTRY) {
      scores[c.key] = c.compute(sub, criteria[c.field]);
    }
    setCriteriaScores(scores);
    setScoringSubapp(sub);
  }, [criteria]);

  const scoringTotal = useMemo(() => {
    return Object.values(criteriaScores).reduce((s, v) => s + v, 0);
  }, [criteriaScores]);

  const saveScoringModal = async () => {
    if (!scoringSubapp) return;
    setScoringSaving(true);
    try {
      await updateSubapplication(scoringSubapp.Rid, { statePriorityScore: String(scoringTotal) });
      notifications.show({ title: 'Score Saved', message: `${scoringSubapp.subappNumber}: score set to ${scoringTotal}`, color: 'green' });
      setScoringSubapp(null);
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to save score', color: 'red' });
    }
    setScoringSaving(false);
  };

  // ── Sort ──
  const [sortField, setSortField] = useState<'finalScore' | 'autoScore' | 'bcaRatio' | 'title'>('finalScore');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // ── Data loading ──
  useEffect(() => { fetchSubapplications(); fetchOrgs(); }, []);

  useEffect(() => {
    (async () => {
      setCriteriaLoading(true);
      try {
        const res = await fetch(`${API}/hmgp-scoring-criteria`, { headers: H });
        if (res.ok) {
          const data = await res.json();
          if (data && typeof data.bcaWeight === 'number') {
            setCriteria(data);
            setCriteriaDraft(data);
          }
        }
      } catch { /* use defaults */ }
      setCriteriaLoading(false);
    })();
  }, []);

  // ── Filter to active disaster ──
  const filtered = useMemo(() => {
    if (!disasterId) return [];
    return subapplications.filter(s => String(s.disasterId) === disasterId);
  }, [subapplications, disasterId]);

  // ── Org lookup helper ──
  const orgName = useCallback((orgId: string) => {
    const org = organizations.find(o => String(o.Rid) === String(orgId));
    return org?.orgName || orgId || '—';
  }, [organizations]);

  // ── Scored + ranked list ──
  const ranked = useMemo(() => {
    const list = filtered.map(sub => {
      const autoScore = computeAutoScore(sub, criteria);
      const finalScore = getFinalScore(sub, criteria);
      return { sub, autoScore, finalScore };
    });

    list.sort((a, b) => {
      if (sortField === 'finalScore') return sortDir === 'desc' ? b.finalScore - a.finalScore : a.finalScore - b.finalScore;
      if (sortField === 'autoScore') return sortDir === 'desc' ? b.autoScore - a.autoScore : a.autoScore - b.autoScore;
      if (sortField === 'bcaRatio') {
        const av = parseFloat(a.sub.bcaRatio) || 0;
        const bv = parseFloat(b.sub.bcaRatio) || 0;
        return sortDir === 'desc' ? bv - av : av - bv;
      }
      if (sortField === 'title') {
        const cmp = (a.sub.subappTitle || '').localeCompare(b.sub.subappTitle || '');
        return sortDir === 'desc' ? -cmp : cmp;
      }
      return 0;
    });

    return list;
  }, [filtered, criteria, sortField, sortDir]);

  // ── Save criteria to KV ──
  const saveCriteria = async () => {
    setCriteriaLoading(true);
    try {
      const total = Object.values(criteriaDraft).reduce((s, v) => s + v, 0);
      if (total !== 100) {
        notifications.show({ title: 'Invalid Weights', message: `Weights must sum to 100 (currently ${total})`, color: 'red' });
        setCriteriaLoading(false);
        return;
      }
      await fetch(`${API}/hmgp-scoring-criteria`, {
        method: 'PUT', headers: H, body: JSON.stringify(criteriaDraft),
      });
      setCriteria(criteriaDraft);
      notifications.show({ title: 'Saved', message: 'Scoring criteria updated', color: 'green' });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to save criteria', color: 'red' });
    }
    setCriteriaLoading(false);
  };

  // ── Save manual score for a subapp ──
  const saveManualScore = async (sub: HMGPSubapplication, scoreStr: string) => {
    setSaving(p => ({ ...p, [sub.Rid]: true }));
    try {
      await updateSubapplication(sub.Rid, { statePriorityScore: scoreStr });
      notifications.show({ title: 'Score Saved', message: `${sub.subappNumber}: score set to ${scoreStr || 'auto'}`, color: 'green' });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to save score', color: 'red' });
    }
    setSaving(p => ({ ...p, [sub.Rid]: false }));
  };

  // ── Prioritize a single subapp ──
  const prioritize = async (sub: HMGPSubapplication, rank: number) => {
    setSaving(p => ({ ...p, [sub.Rid]: true }));
    try {
      await updateSubapplication(sub.Rid, {
        status: 'prioritized',
        statePriorityRank: String(rank),
      });
      notifications.show({ title: 'Prioritized', message: `${sub.subappNumber} ranked #${rank}`, color: 'teal' });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to prioritize', color: 'red' });
    }
    setSaving(p => ({ ...p, [sub.Rid]: false }));
  };

  // ── Forward to FEMA ──
  const forwardToFEMA = async (sub: HMGPSubapplication) => {
    setSaving(p => ({ ...p, [sub.Rid]: true }));
    try {
      await updateSubapplication(sub.Rid, { status: 'forwarded_to_fema' });
      notifications.show({ title: 'Forwarded', message: `${sub.subappNumber} forwarded to FEMA`, color: 'green' });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to forward', color: 'red' });
    }
    setSaving(p => ({ ...p, [sub.Rid]: false }));
  };

  // ── Bulk prioritize top N ──
  const bulkPrioritize = async () => {
    const n = typeof bulkN === 'number' ? bulkN : 10;
    const top = ranked.slice(0, n);
    for (let i = 0; i < top.length; i++) {
      const { sub } = top[i];
      if (sub.status === 'forwarded_to_fema') continue;
      await updateSubapplication(sub.Rid, {
        status: 'prioritized',
        statePriorityRank: String(i + 1),
      });
    }
    await fetchSubapplications();
    notifications.show({ title: 'Bulk Prioritized', message: `Top ${Math.min(n, top.length)} subapplications prioritized`, color: 'teal' });
  };

  // ── Bulk forward all prioritized ──
  const bulkForward = async () => {
    const toForward = filtered.filter(s => s.status === 'prioritized');
    if (toForward.length === 0) {
      notifications.show({ title: 'Nothing to Forward', message: 'No prioritized subapplications found', color: 'yellow' });
      return;
    }
    for (const sub of toForward) {
      await updateSubapplication(sub.Rid, { status: 'forwarded_to_fema' });
    }
    await fetchSubapplications();
    notifications.show({ title: 'Forwarded', message: `${toForward.length} subapplications forwarded to FEMA`, color: 'green' });
  };

  // ── CSV export ──
  const handleExport = () => {
    const headers = ['Rank', 'Subapp #', 'Title', 'Applicant', 'Type', 'BCA Ratio', 'Auto Score', 'Manual Score', 'Final Score', 'Status'];
    const rows = ranked.map(({ sub, autoScore, finalScore }, i) => [
      String(i + 1),
      sub.subappNumber,
      sub.subappTitle,
      orgName(sub.organizationId),
      HMGP_PROJECT_TYPE_LABELS[sub.projectType as HMGPProjectType] || sub.projectType,
      sub.bcaRatio || '—',
      String(autoScore),
      sub.statePriorityScore || '',
      String(finalScore),
      HMGP_SUBAPP_STATUS_LABELS[sub.status as HMGPSubappStatus] || sub.status,
    ]);
    exportToCSV('hmgp-priority-ranking.csv', headers, rows);
  };

  // ── Sort toggle ──
  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const sortIcon = (field: typeof sortField) => {
    if (sortField !== field) return <IconMinus size={14} style={{ opacity: 0.3 }} />;
    return sortDir === 'desc' ? <IconArrowDown size={14} /> : <IconArrowUp size={14} />;
  };

  // ── Empty states ──
  if (!activeDisaster) {
    return (
      <Stack p="md" align="center" mt="xl">
        <Title order={3}>State Priority Scoring</Title>
        <Text c="dimmed">Select an incident to view subapplication scoring.</Text>
      </Stack>
    );
  }

  const totalMaxPts = Object.values(criteria).reduce((s, v) => s + v, 0);
  const prioritizedCount = filtered.filter(s => s.status === 'prioritized').length;
  const forwardedCount = filtered.filter(s => s.status === 'forwarded_to_fema').length;

  return (
    <Stack p="md" gap="md">
      {/* Header */}
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={3}>State Priority Scoring</Title>
          <Text size="sm" c="dimmed">
            Score and rank HMGP subapplications for {activeDisaster.disasterName || 'active disaster'}
          </Text>
        </div>
        <Group gap="xs">
          <Button
            variant="light"
            size="xs"
            color="gray"
            leftSection={<IconSettings size={16} />}
            onClick={() => setCriteriaOpen(o => !o)}
          >
            {criteriaOpen ? 'Hide' : 'Edit'} Criteria
          </Button>
          <Tooltip label="Refresh data">
            <ActionIcon variant="light" onClick={() => fetchSubapplications()}>
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      {/* Stat cards */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
        <Paper p="sm" withBorder ta="center" style={{ borderTop: `3px solid ${HMGP_GREEN}` }}>
          <Text size="xs" c="dimmed">Total Subapps</Text>
          <Text size="xl" fw={700}>{filtered.length}</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #0d9488' }}>
          <Text size="xs" c="dimmed">Prioritized</Text>
          <Text size="xl" fw={700} c="teal">{prioritizedCount}</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #16a34a' }}>
          <Text size="xs" c="dimmed">Forwarded to FEMA</Text>
          <Text size="xl" fw={700} c="green">{forwardedCount}</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #6366f1' }}>
          <Text size="xs" c="dimmed">Max Score</Text>
          <Text size="xl" fw={700}>{totalMaxPts}</Text>
        </Paper>
      </SimpleGrid>

      {/* Criteria configuration panel */}
      <Collapse in={criteriaOpen}>
        <Paper p="md" withBorder style={{ borderColor: HMGP_GREEN }}>
          <Stack gap="sm">
            <Group justify="space-between">
              <Text fw={600}>Scoring Criteria Weights</Text>
              <Text size="xs" c="dimmed">Total must equal 100</Text>
            </Group>
            <SimpleGrid cols={{ base: 2, sm: 3, md: 6 }} spacing="sm">
              {(Object.keys(CRITERIA_LABELS) as (keyof ScoringCriteria)[]).map(key => (
                <Tooltip key={key} label={CRITERIA_DESCRIPTIONS[key]} multiline w={240}>
                  <NumberInput
                    label={CRITERIA_LABELS[key]}
                    size="xs"
                    min={0}
                    max={100}
                    value={criteriaDraft[key]}
                    onChange={v => setCriteriaDraft(prev => ({ ...prev, [key]: typeof v === 'number' ? v : 0 }))}
                  />
                </Tooltip>
              ))}
            </SimpleGrid>
            <Group gap="xs">
              <Button
                size="xs"
                color="teal"
                leftSection={<IconDeviceFloppy size={16} />}
                onClick={saveCriteria}
                loading={criteriaLoading}
              >
                Save Criteria
              </Button>
              <Button
                size="xs"
                variant="subtle"
                color="gray"
                onClick={() => setCriteriaDraft(DEFAULT_CRITERIA)}
              >
                Reset Defaults
              </Button>
              <Text size="xs" c="dimmed" ml="auto">
                Current sum: {Object.values(criteriaDraft).reduce((s, v) => s + v, 0)}
              </Text>
            </Group>
          </Stack>
        </Paper>
      </Collapse>

      {/* Bulk actions */}
      <Paper p="sm" withBorder>
        <Group gap="sm" wrap="wrap">
          <Group gap={4}>
            <Text size="sm" fw={500}>Prioritize Top</Text>
            <NumberInput
              size="xs"
              w={60}
              min={1}
              max={filtered.length || 100}
              value={bulkN}
              onChange={v => setBulkN(typeof v === 'number' ? v : '')}
            />
            <Button
              size="xs"
              color="teal"
              leftSection={<IconRocket size={16} />}
              onClick={bulkPrioritize}
              disabled={ranked.length === 0}
            >
              Prioritize
            </Button>
          </Group>
          <Button
            size="xs"
            color="green"
            leftSection={<IconSend size={16} />}
            onClick={bulkForward}
            disabled={prioritizedCount === 0}
          >
            Forward All Prioritized ({prioritizedCount})
          </Button>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconDownload size={16} />}
            onClick={handleExport}
            disabled={ranked.length === 0}
            ml="auto"
          >
            Export CSV
          </Button>
        </Group>
      </Paper>

      {/* Scoring table */}
      {loadingSubs ? (
        <Center py="xl"><Loader color="teal" /></Center>
      ) : ranked.length === 0 ? (
        <Paper p="xl" withBorder ta="center">
          <Text c="dimmed">No subapplications found for this disaster.</Text>
        </Paper>
      ) : (
        <Paper withBorder style={{ overflowX: 'auto' }}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 50 }}>#</Table.Th>
                <Table.Th>Subapp #</Table.Th>
                <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('title')}>
                  <Group gap={4}>Title {sortIcon('title')}</Group>
                </Table.Th>
                <Table.Th>Applicant</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('bcaRatio')}>
                  <Group gap={4}>BCA Ratio {sortIcon('bcaRatio')}</Group>
                </Table.Th>
                <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('autoScore')}>
                  <Group gap={4}>Auto Score {sortIcon('autoScore')}</Group>
                </Table.Th>
                <Table.Th>Manual Override</Table.Th>
                <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('finalScore')}>
                  <Group gap={4}>Final Score {sortIcon('finalScore')}</Group>
                </Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {ranked.map(({ sub, autoScore, finalScore }, idx) => {
                const rank = idx + 1;
                const localManual = manualScores[sub.Rid] ?? sub.statePriorityScore ?? '';
                const isManualOverride = !!(parseFloat(sub.statePriorityScore) > 0);
                const statusColor = SUBAPP_STATUS_COLORS[sub.status] || 'gray';
                const typeLabel = HMGP_PROJECT_TYPE_LABELS[sub.projectType as HMGPProjectType] || sub.projectType;
                const typeColor = HMGP_PROJECT_TYPE_COLORS[sub.projectType as HMGPProjectType] || '#6B7280';

                return (
                  <Table.Tr key={sub.Rid} style={{ cursor: 'pointer' }} onClick={() => openScoring(sub)}>
                    <Table.Td>
                      <Text fw={700} c={rank <= 3 ? 'teal' : undefined}>
                        {rank}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" ff="monospace">{sub.subappNumber || '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" lineClamp={1} maw={200}>{sub.subappTitle || '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" lineClamp={1} maw={150}>{orgName(sub.organizationId)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="xs" variant="light" style={{ backgroundColor: typeColor + '18', color: typeColor }}>
                        {typeLabel}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {sub.bcaRatio ? parseFloat(sub.bcaRatio).toFixed(2) : '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Tooltip
                        multiline
                        w={260}
                        label={
                          <Stack gap={2}>
                            {CRITERIA_REGISTRY.map(c => {
                              const pts = c.compute(sub, criteria[c.field]);
                              const max = criteria[c.field];
                              return (
                                <Group key={c.key} justify="space-between" gap="xs">
                                  <Text size="xs" inherit>{CRITERIA_LABELS[c.field]}</Text>
                                  <Text size="xs" inherit fw={600}>{pts}/{max}</Text>
                                </Group>
                              );
                            })}
                          </Stack>
                        }
                      >
                        <Text size="sm" c="dimmed">{autoScore}/{totalMaxPts}</Text>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td onClick={e => e.stopPropagation()}>
                      <Group gap={4} wrap="nowrap">
                        <TextInput
                          size="xs"
                          w={60}
                          placeholder="auto"
                          value={localManual}
                          onChange={e => setManualScores(p => ({ ...p, [sub.Rid]: e.target.value }))}
                          onBlur={() => {
                            const val = manualScores[sub.Rid];
                            if (val !== undefined && val !== sub.statePriorityScore) {
                              saveManualScore(sub, val);
                            }
                          }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              const val = manualScores[sub.Rid];
                              if (val !== undefined) saveManualScore(sub, val);
                            }
                          }}
                        />
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={700} c={isManualOverride ? 'orange' : HMGP_GREEN}>
                        {finalScore}
                        {isManualOverride && <Text span size="xs" c="dimmed" ml={2}>(manual)</Text>}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="xs" color={statusColor} variant="light">
                        {HMGP_SUBAPP_STATUS_LABELS[sub.status as HMGPSubappStatus] || sub.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td onClick={e => e.stopPropagation()}>
                      <Group gap={4} wrap="nowrap">
                        <Tooltip label="Edit Score Breakdown">
                          <ActionIcon
                            size="sm"
                            variant="light"
                            color="blue"
                            onClick={() => openScoring(sub)}
                          >
                            <IconPencil size={14} />
                          </ActionIcon>
                        </Tooltip>
                        {sub.status !== 'prioritized' && sub.status !== 'forwarded_to_fema' && (
                          <Tooltip label={`Prioritize as #${rank}`}>
                            <ActionIcon
                              size="sm"
                              variant="light"
                              color="teal"
                              loading={saving[sub.Rid]}
                              onClick={() => prioritize(sub, rank)}
                            >
                              <IconRocket size={14} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                        {sub.status === 'prioritized' && (
                          <Tooltip label="Forward to FEMA">
                            <ActionIcon
                              size="sm"
                              variant="light"
                              color="green"
                              loading={saving[sub.Rid]}
                              onClick={() => forwardToFEMA(sub)}
                            >
                              <IconSend size={14} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {/* ── Score Detail Modal ── */}
      <Modal
        opened={!!scoringSubapp}
        onClose={() => setScoringSubapp(null)}
        title={
          scoringSubapp ? (
            <div>
              <Text fw={700} size="lg">Score Breakdown</Text>
              <Text size="sm" c="dimmed">{scoringSubapp.subappNumber} — {scoringSubapp.subappTitle}</Text>
            </div>
          ) : 'Score Breakdown'
        }
        size="lg"
      >
        {scoringSubapp && (
          <Stack gap="md">
            {CRITERIA_REGISTRY.map(c => {
              const maxPts = criteria[c.field];
              const autoVal = c.compute(scoringSubapp, maxPts);
              const current = criteriaScores[c.key] ?? autoVal;
              const pct = maxPts > 0 ? (current / maxPts) * 100 : 0;

              return (
                <Paper key={c.key} p="sm" withBorder>
                  <Group justify="space-between" mb={4}>
                    <div>
                      <Text size="sm" fw={600}>{CRITERIA_LABELS[c.field]}</Text>
                      <Text size="xs" c="dimmed">{CRITERIA_DESCRIPTIONS[c.field]}</Text>
                    </div>
                    <Badge size="sm" variant="light" color="gray">
                      Auto: {autoVal}
                    </Badge>
                  </Group>
                  <Progress
                    value={pct}
                    color={pct >= 75 ? 'teal' : pct >= 40 ? 'yellow' : 'red'}
                    size="sm"
                    mb="xs"
                  />
                  <Group gap="sm" align="center">
                    <Slider
                      min={0}
                      max={maxPts}
                      step={1}
                      value={current}
                      onChange={v => setCriteriaScores(prev => ({ ...prev, [c.key]: v }))}
                      label={v => `${v}/${maxPts}`}
                      style={{ flex: 1 }}
                      color="teal"
                    />
                    <NumberInput
                      size="xs"
                      w={70}
                      min={0}
                      max={maxPts}
                      value={current}
                      onChange={v => setCriteriaScores(prev => ({ ...prev, [c.key]: typeof v === 'number' ? v : 0 }))}
                    />
                    <Text size="xs" c="dimmed" w={30} ta="right">/{maxPts}</Text>
                  </Group>
                </Paper>
              );
            })}

            {/* Total */}
            <Paper p="sm" withBorder style={{ borderColor: HMGP_GREEN, backgroundColor: '#f0fdf4' }}>
              <Group justify="space-between">
                <Text fw={700} size="lg">Total Score</Text>
                <Text fw={700} size="xl" c={HMGP_GREEN}>{scoringTotal} / {totalMaxPts}</Text>
              </Group>
            </Paper>

            <Group justify="flex-end" gap="sm">
              <Button variant="subtle" color="gray" onClick={() => setScoringSubapp(null)}>
                Cancel
              </Button>
              <Button
                color="teal"
                leftSection={<IconDeviceFloppy size={16} />}
                onClick={saveScoringModal}
                loading={scoringSaving}
              >
                Save Score ({scoringTotal})
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
